import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import { ArrowBack } from '@mui/icons-material';
import { GetTickets } from '../../../axios/Axios';
import { useUser } from '../../dashboard-components/UserContext';
import TicketListPane from './TicketListPane';
import TicketChatPane from './TicketChatPane';
import CreateTicketModal from './CreateTicketModal';

export interface TicketItem {
    id: number;
    ticket_number: string;
    subject: string;
    category: string;
    reason: string;
    priority: string;
    status: string;
    franchise_name: string;
    created_by_name: string;
    assigned_to_name: string | null;
    last_message_preview: string | null;
    last_message_at: string | null;
    unread: boolean;
    message_count: number;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
}

export interface TicketFilters {
    status: string;
    category: string;
    priority: string;
    assigned_to: string;
    search: string;
}

const DEFAULT_FILTERS: TicketFilters = {
    status: 'All',
    category: 'All',
    priority: 'All',
    assigned_to: '',
    search: '',
};

export default function SupportTickets() {
    const { user } = useUser();
    const isSuperAdmin = user?.location_permissions?.includes('super_admin');

    const [tickets, setTickets] = useState<TicketItem[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState<TicketFilters>(DEFAULT_FILTERS);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const fetchTickets = useCallback(async (pageNum: number, currentFilters: TicketFilters, append = false) => {
        setIsLoading(true);
        try {
            const params: any = { page: pageNum };
            if (currentFilters.status !== 'All') params.status = currentFilters.status;
            if (currentFilters.category !== 'All') params.category = currentFilters.category;
            if (currentFilters.priority !== 'All') params.priority = currentFilters.priority;
            if (currentFilters.assigned_to) params.assigned_to = currentFilters.assigned_to;
            if (currentFilters.search) params.search = currentFilters.search;

            const response = await GetTickets(params);
            const { tickets: newTickets, hasMore: more } = response.data;

            setTickets(prev => append ? [...prev, ...newTickets] : newTickets);
            setHasMore(more);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        setPage(1);
        fetchTickets(1, filters);
    }, [filters, fetchTickets]);

    const loadMore = useCallback(() => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchTickets(nextPage, filters, true);
    }, [page, filters, fetchTickets]);

    const handleSelectTicket = useCallback((ticket: TicketItem) => {
        setSelectedTicket(ticket);
    }, []);

    const handleBack = useCallback(() => {
        setSelectedTicket(null);
    }, []);

    const handleFiltersChange = useCallback((newFilters: TicketFilters) => {
        setFilters(newFilters);
    }, []);

    const handleTicketCreated = useCallback((newTicket: any) => {
        setCreateModalOpen(false);
        setFilters(DEFAULT_FILTERS);
        setPage(1);
        fetchTickets(1, DEFAULT_FILTERS).then(() => {
            setSelectedTicket({
                id: newTicket.id,
                ticket_number: newTicket.ticket_number,
                subject: newTicket.subject,
                category: newTicket.category,
                reason: newTicket.reason || '',
                priority: newTicket.priority,
                status: newTicket.status,
                franchise_name: newTicket.franchise_name || '',
                created_by_name: '',
                assigned_to_name: null,
                last_message_preview: null,
                last_message_at: newTicket.created_at,
                unread: false,
                message_count: 1,
                created_at: newTicket.created_at,
                updated_at: newTicket.updated_at,
                closed_at: null,
            });
        });
    }, [fetchTickets]);

    const handleTicketUpdated = useCallback(() => {
        fetchTickets(1, filters);
    }, [fetchTickets, filters]);

    const refreshList = useCallback(() => {
        setPage(1);
        fetchTickets(1, filters);
    }, [fetchTickets, filters]);

    return (
        <Sheet
            sx={{
                flex: 1,
                width: '100%',
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    md: 'min(380px, 35%) 1fr',
                },
                overflow: 'hidden',
                height: 'calc(100dvh - var(--Header-height))',
            }}
        >
            {/* Left panel: ticket list */}
            <Sheet
                sx={{
                    borderRight: { md: '1px solid' },
                    borderColor: { md: 'divider' },
                    display: {
                        xs: selectedTicket ? 'none' : 'flex',
                        md: 'flex',
                    },
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <TicketListPane
                    tickets={tickets}
                    selectedTicket={selectedTicket}
                    onSelect={handleSelectTicket}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    hasMore={hasMore}
                    isLoading={isLoading}
                    onLoadMore={loadMore}
                    onCreateNew={() => setCreateModalOpen(true)}
                    isSuperAdmin={!!isSuperAdmin}
                />
            </Sheet>

            {/* Right panel: ticket chat */}
            <Sheet
                sx={{
                    display: {
                        xs: selectedTicket ? 'flex' : 'none',
                        md: 'flex',
                    },
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {selectedTicket && (
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <IconButton size="sm" variant="plain" onClick={handleBack}>
                            <ArrowBack />
                        </IconButton>
                        <Typography level="title-sm" sx={{ ml: 1, alignSelf: 'center' }}>
                            Back to tickets
                        </Typography>
                    </Box>
                )}
                <TicketChatPane
                    ticket={selectedTicket}
                    isSuperAdmin={!!isSuperAdmin}
                    onTicketUpdated={handleTicketUpdated}
                    refreshList={refreshList}
                />
            </Sheet>

            <CreateTicketModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreated={handleTicketCreated}
                isSuperAdmin={!!isSuperAdmin}
            />
        </Sheet>
    );
}
